import * as Api from '@ViewModels';
import { useMutation } from '@tanstack/react-query';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import { AUTOMATION_STEP_MUTATION_SCOPE, AUTOMATION_TEMPLATE_MUTATION_KEY } from '../../queryKeys';
import { invalidateAutomationTemplateDraftStateQuery } from '../../queries/useAutomationTemplateHasDraftQuery';
import { invalidateAutomationTemplateQuery } from '../../queries/useAutomationTemplateQuery';

interface Params {
	templateId: string;
	stepId: string;
	caseId: string;
	caseData: Api.IAutomationStepCaseStatement;
	impersonationContext?: Api.IImpersonationContext;
	/**
	 * Additional template queries that should be invalidated after this case update.
	 * Used by the canvas editor when a nested branch change should also refresh the
	 * root template's draft/published state.
	 */
	relatedTemplateIds?: string[];
}

export function useAutomationTemplateStepSwitchUpdateCaseMutation({
	onSuccess,
	onError,
}: {
	onSuccess?: (data: Api.ISwitchAutomationStep, variables: Params) => void;
	onError?: (error: Api.IOperationResultNoValue, variables: Params) => void;
} = {}) {
	const userSession = useUserSession();
	return useMutation({
		mutationKey: AUTOMATION_TEMPLATE_MUTATION_KEY,
		scope: AUTOMATION_STEP_MUTATION_SCOPE,
		mutationFn: ({ templateId, stepId, caseId, caseData, impersonationContext }: Params) => {
			return userSession.webServiceHelper.callAsync<Api.ISwitchAutomationStep>(
				Api.ImpersonationBroker.composeApiUrl({
					urlPath: `AutomationTemplate/${encodeURIComponent(templateId)}/Step/Switch/${encodeURIComponent(
						stepId
					)}/Case/${encodeURIComponent(caseId)}`,
					impersonationContext,
				}),
				'PUT',
				caseData
			);
		},
		onError,
		onSuccess: (data, variables) => {
			const templateIdsToInvalidate = new Set<string>([variables.templateId, ...(variables.relatedTemplateIds ?? [])]);
			if (variables.caseData.automationTemplateId) {
				templateIdsToInvalidate.add(variables.caseData.automationTemplateId);
			}
			for (const templateId of templateIdsToInvalidate) {
				invalidateAutomationTemplateQuery(templateId);
				invalidateAutomationTemplateDraftStateQuery(templateId);
			}
			onSuccess?.(data, variables);
		},
	});
}
